import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { SettingsProvider } from "@/lib/settings";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import NewStartsCalendar from "@/pages/NewStartsCalendar";
import TicketsList from "@/pages/TicketsList";
import TicketDetail from "@/pages/TicketDetail";
import Inbox from "@/pages/Inbox";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Settings from "@/pages/Settings";
import Insights from "@/pages/Insights";
import { PortalLogin, PortalTickets, PortalTicketDetail, PortalMySubmissions, PortalNewStarts, PortalSubmitForm, PortalVerify } from "@/pages/Portal";
import PublicTicket from "@/pages/PublicTicket";
import AcceptInvite from "@/pages/AcceptInvite";
import AdminForms from "@/pages/AdminForms";
import FormBuilder from "@/pages/FormBuilder";
import PublicForm from "@/pages/PublicForm";
import Approve from "@/pages/Approve";
import AdminSchedule from "@/pages/AdminSchedule";
import EmailLog from "@/pages/EmailLog";
import ThreadingLog from "@/pages/ThreadingLog";
import Team from "@/pages/Team";

/**
 * MSAL popup landing page. It renders a static message while MSAL in the
 * opener window reads the auth response from this popup's URL and closes it.
 * Crucially, this route does NOT redirect anywhere so MSAL can complete
 * its handshake without a race against React Router.
 */
function AuthCallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center space-y-2">
        <div className="text-sm text-slate-700 font-heading">Signing you in…</div>
        <div className="text-xs text-slate-500 font-mono">
          This window will close automatically.
        </div>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

function PortalProtected({ children }) {
  const token = localStorage.getItem("hd_portal_token");
  if (!token) return <Navigate to="/portal" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/portal" element={<PortalLogin />} />
              <Route path="/portal/verify" element={<PortalVerify />} />
              <Route path="/portal/t/:token" element={<PublicTicket />} />
              <Route path="/accept-invite/:token" element={<AcceptInvite />} />
              <Route path="/portal/tickets" element={<PortalProtected><PortalTickets /></PortalProtected>} />
              <Route path="/portal/tickets/:id" element={<PortalProtected><PortalTicketDetail /></PortalProtected>} />
              <Route path="/portal/submissions" element={<PortalProtected><PortalMySubmissions /></PortalProtected>} />
              <Route path="/portal/new-starts" element={<PortalProtected><PortalNewStarts /></PortalProtected>} />
              <Route path="/portal/submit" element={<PortalProtected><PortalSubmitForm /></PortalProtected>} />
              <Route path="/forms/:slug" element={<PublicForm />} />
              <Route path="/approve/:token" element={<Approve />} />
              <Route path="/" element={<Protected><Dashboard /></Protected>} />
              <Route path="/tickets" element={<Protected><TicketsList /></Protected>} />
              <Route path="/tickets/:id" element={<Protected><TicketDetail /></Protected>} />
              <Route path="/insights" element={<Protected><Insights /></Protected>} />
              <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
              <Route path="/kb" element={<Protected><KnowledgeBase /></Protected>} />
              <Route path="/admin/forms" element={<Protected><AdminForms /></Protected>} />
              <Route path="/admin/forms/:id" element={<Protected><FormBuilder /></Protected>} />
              <Route path="/admin/schedule" element={<Protected><AdminSchedule /></Protected>} />
              <Route path="/admin/email-log" element={<Protected><EmailLog /></Protected>} />
              <Route path="/admin/threading-log" element={<Protected><ThreadingLog /></Protected>} />
              <Route path="/new-starts" element={<Protected><NewStartsCalendar /></Protected>} />
              <Route path="/offboards" element={<Protected><NewStartsCalendar
                fetchUrl="/offboards"
                title="Offboards"
                subtitle="Every offboarding form submission plotted against the leaver's final day. Click a name to open the ticket."
                upcomingLabel="Upcoming leavers"
                undatedLabel="Awaiting a leaving date"
                undatedHelp="These offboarding requests were submitted before the final day was known. They'll appear on the calendar as soon as the requester adds a date."
              /></Protected>} />
              <Route path="/team" element={<Protected><Team /></Protected>} />
              <Route path="/settings" element={<Protected><Settings /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
